import { FC, ReactNode } from 'react';
import { Box, Stack } from '@mui/material';

import { MplBottomSheet, SkeletonBlock } from '@mopla/ui';

/** Number of zone-tile placeholders — mirrors the common multi-zone catalog. */
const ZONE_TILE_COUNT = 5;

const IconSkeleton = () => <SkeletonBlock w={24} h={24} r={12} />;

const SkeletonSection: FC<{
	titleWidth: number;
	withTopDivider?: boolean;
	children: ReactNode;
}> = ({ titleWidth, withTopDivider = false, children }) => (
	<Box
		sx={{
			width: 'auto',
			mx: -2,
			pt: 2,
			pb: 1,
			px: 2,
			...(withTopDivider && {
				borderTop: (theme: { palette: { neutral?: { 200?: string } } }) =>
					`1px solid ${theme.palette.neutral?.[200] ?? '#b2b2b2'}`,
			}),
		}}
	>
		<Stack sx={{ alignItems: 'flex-start', width: '100%' }}>
			<Stack
				direction="row"
				alignItems="center"
				gap={0.5}
				sx={{ minHeight: 24, width: '100%' }}
			>
				<IconSkeleton />
				<SkeletonBlock w={titleWidth} h={20} r={4} />
			</Stack>
			{children}
		</Stack>
	</Box>
);

const CollapsedSubtitle: FC<{ children: ReactNode }> = ({ children }) => (
	<Box sx={{ width: '100%', pt: 1 }}>{children}</Box>
);

/**
 * Loading skeleton for the operator ticket detail page. Mirrors the default
 * resolved layout — user-info collapsed, zones section open, validity and
 * payment collapsed — plus the static bottom sheet, so no layout shift occurs
 * when the real content resolves.
 */
export const OperatorTicketDetailSkeleton = () => (
	<>
		<Stack
			sx={{
				width: '100%',
				// Match the resolved content wrapper (see OperatorTicketDetail).
				mt: '-16px',
				pb: '160px',
			}}
			data-testid="operator-ticket-detail-skeleton"
		>
			{/* User info — collapsed */}
			<SkeletonSection titleWidth={120}>
				<CollapsedSubtitle>
					<SkeletonBlock w={180} h={16} r={4} />
				</CollapsedSubtitle>
			</SkeletonSection>

			{/* Zones — open by default */}
			<SkeletonSection titleWidth={80} withTopDivider>
				<Stack gap={1} sx={{ width: '100%', pt: 1 }}>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: '1fr',
							gap: 1,
							width: '100%',
							pb: 2,
						}}
					>
						{Array.from({ length: ZONE_TILE_COUNT }).map((_, i) => (
							<SkeletonBlock key={i} w="100%" h={70} r={8} />
						))}
					</Box>
					<SkeletonBlock w={140} h={16} r={4} />
					<Stack
						direction="row"
						alignItems="center"
						gap={1}
						sx={{ width: '100%' }}
					>
						<IconSkeleton />
						<Stack gap={0.5} sx={{ flex: 1 }}>
							<SkeletonBlock w="100%" h={14} r={4} />
							<SkeletonBlock w="60%" h={14} r={4} />
						</Stack>
					</Stack>
				</Stack>
			</SkeletonSection>

			{/* Validity — collapsed (two-line summary) */}
			<SkeletonSection titleWidth={110} withTopDivider>
				<CollapsedSubtitle>
					<Stack gap={0.25}>
						<SkeletonBlock w={150} h={18} r={4} />
						<SkeletonBlock w={200} h={16} r={4} />
					</Stack>
				</CollapsedSubtitle>
			</SkeletonSection>

			{/* Payment — collapsed */}
			<SkeletonSection titleWidth={100} withTopDivider>
				<CollapsedSubtitle>
					<SkeletonBlock w={110} h={18} r={4} />
				</CollapsedSubtitle>
			</SkeletonSection>
		</Stack>

		<MplBottomSheet
			open
			isStatic
			testId="operator-ticket-detail-skeleton-bottom-sheet"
		>
			<Stack gap={2} sx={{ pt: 2, px: 2 }}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<SkeletonBlock w={140} h={24} r={4} />
					<SkeletonBlock w={70} h={20} r={4} />
				</Stack>
				<SkeletonBlock w="100%" h={48} r={8} />
			</Stack>
		</MplBottomSheet>
	</>
);
